<template>
  <div 
    v-if="open" 
    class="notification-panel"
    :class="{ 'mobile': isMobile }"
  >
    <div class="notification-header">
      <span>实时通知</span>
      <div class="header-actions">
        <a-button 
          type="text" 
          size="small" 
          @click="markAllRead"
          :disabled="unreadCount === 0"
        >
          全部已读
        </a-button>
        <a-button 
          type="text" 
          size="small" 
          @click="togglePanel"
        >
          <CloseOutlined />
        </a-button>
      </div>
    </div>
    
    <div class="notification-list">
      <div 
        v-for="notification in notifications.slice(0, 50)" 
        :key="notification.id"
        class="notification-item"
        :class="{ 'unread': !notification.is_read }"
        @click="handleNotificationClick(notification)"
      >
        <div class="notification-content">
          <div class="notification-title">
            {{ notification.title }}
          </div>
          <div class="notification-message">
            {{ notification.message }}
          </div>
          <div class="notification-time">
            {{ formatTime(notification.created_at) }}
          </div>
        </div>
        <div v-if="!notification.is_read" class="unread-dot"></div>
      </div>
      
      <div v-if="notifications.length === 0" class="empty-state">
        <BellOutlined />
        <p>暂无通知</p>
      </div>
    </div>
    
    <div class="notification-footer">
      <div class="footer-info">
        <span class="info-text">仅显示最新50条通知</span>
      </div>
      <a-button type="link" size="small" @click="viewAllNotifications">
        查看全部通知
      </a-button>
    </div>
  </div>

  <!-- 通知按钮 -->
  <a-button 
    v-if="!open"
    type="primary" 
    shape="circle" 
    size="large"
    class="notification-trigger"
    @click="togglePanel"
  >
    <a-badge :count="displayUnreadCount" :offset="[-8, 8]" :overflow-count="9999">
      <BellOutlined />
    </a-badge>
  </a-button>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { message } from 'ant-design-vue'
import {
  BellOutlined,
  CloseOutlined
} from '@ant-design/icons-vue'
import { useUserStore } from '@/store/user'
import api from '@/services/api'
import { eventBus, EVENTS } from '@/utils/eventBus'

// ---- 通知提示音：优先播放 /massage.mp3，失败则用 WebAudio 兜底 ----
let audioUnlocked = false
let beepCtx = null

const unlockAudio = () => {
  if (audioUnlocked) return
  // 预创建 AudioContext 以通过用户首次交互解锁
  try {
    // @ts-ignore
    beepCtx = new (window.AudioContext || window.webkitAudioContext)()
    if (beepCtx && beepCtx.state === 'suspended') {
      beepCtx.resume().catch(() => {})
    }
  } catch (e) {
    beepCtx = null
  }
  audioUnlocked = true
}

const playBeepFallback = () => {
  try {
    if (!beepCtx) {
      // @ts-ignore
      beepCtx = new (window.AudioContext || window.webkitAudioContext)()
    }
    const duration = 0.15
    const oscillator = beepCtx.createOscillator()
    const gainNode = beepCtx.createGain()
    oscillator.type = 'sine'
    oscillator.frequency.value = 880
    gainNode.gain.value = 0.15
    oscillator.connect(gainNode)
    gainNode.connect(beepCtx.destination)
    oscillator.start()
    oscillator.stop(beepCtx.currentTime + duration)
  } catch (e) {
    // 忽略兜底失败
  }
}

const playNotificationSound = async () => {
  // 尝试播放根路径的音频文件（由服务器提供 /massage.mp3）
  try {
    const audio = new Audio('/massage.mp3')
    audio.preload = 'auto'
    audio.volume = 0.6
    audio.currentTime = 0
    await audio.play()
    return
  } catch (e) {
    // 被策略拦截或资源不存在，降级为 beep
    playBeepFallback()
  }
}

// 用户交互时解锁音频
window.addEventListener('click', unlockAudio, { once: true })
window.addEventListener('touchstart', unlockAudio, { once: true })
// ---- 提示音逻辑结束 ----

const router = useRouter()
const userStore = useUserStore()

// 响应式数据
const open = ref(false)
const notifications = ref([])
const isMobile = ref(false)
const ws = ref(null)

// 计算属性
const unreadOverride = ref(null) // 历史未读数量覆盖
const unreadCount = computed(() => {
  return notifications.value.filter(n => !n.is_read).length
})
const displayUnreadCount = computed(() => {
  return unreadOverride.value ?? unreadCount.value
})

// WebSocket连接
const connectWebSocket = () => {
  if (!userStore.isAuthenticated) return

  // 获取认证令牌
  const tokens = JSON.parse(localStorage.getItem('tokens') || '{}')
  const accessToken = tokens.access

  if (!accessToken) {
    console.error('未找到访问令牌')
    return
  }

  // 使用代理路径：强制同源优先，避免直连 8000 受限
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  // 忽略 VITE_WS_URL，统一走同源，通过 Vite 代理 /ws 到后端
  const wsBase = `${protocol}//${window.location.host}`
  const wsUrl = `${wsBase}/ws/notifications/?token=${accessToken}`
  
  console.log('尝试连接WebSocket:', wsUrl)
  
  ws.value = new WebSocket(wsUrl)
  
  ws.value.onopen = () => {
    console.log('WebSocket连接已建立')
  }
  
  ws.value.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data)
      console.log('收到WebSocket消息:', data)
      handleWebSocketMessage(data)
    } catch (error) {
      console.error('解析WebSocket消息失败:', error)
    }
  }
  
  ws.value.onclose = () => {
    console.log('WebSocket连接已关闭')
    // 尝试重连
    setTimeout(() => {
      if (userStore.isAuthenticated) {
        connectWebSocket()
      }
    }, 3000)
  }
  
  ws.value.onerror = (error) => {
    console.error('WebSocket连接错误:', error)
  }
}

// 处理WebSocket消息
const handleWebSocketMessage = (data) => {
  console.log('收到WebSocket消息:', data)
  
  // 系统消息：连接建立或历史未读
  if (data.type === 'connection_established' || data.type === 'unread_notifications') {
    if (data.type === 'unread_notifications') {
      // 历史未读：仅更新徽标数量，不逐条入列、不触发刷新
      if (Array.isArray(data.notifications)) {
        unreadOverride.value = data.notifications.length
      } else if (typeof data.count === 'number') {
        unreadOverride.value = data.count
      }
    }
    return
  }
  
  // 实时通知：仅包含 id/title 的才处理
  if (data.id && data.title) {
    const notification = {
      ...data,
      is_read: false
    }
    
    // 只看个人时，过滤掉门店层级或与本人无关的通知
    const onlyPersonal = userStore?.onlyPersonal === true
    if (onlyPersonal) {
      const currentUserId = userStore?.user?.id
      const isStoreLevel = notification?.type === 'store_new_ticket'
      const isPersonalRelated =
        (notification?.assignee_id && notification.assignee_id === currentUserId) ||
        (notification?.handler_id && notification.handler_id === currentUserId) ||
        (notification?.creator_id && notification.creator_id === currentUserId) ||
        (Array.isArray(notification?.related_user_ids) && notification.related_user_ids.includes(currentUserId))
      if (isStoreLevel || !isPersonalRelated) {
        return
      }
    }
    // 通知归属与类型判断：
    // - 完成事件：任何人完成的工单，全部忽略（不推送、不提示、不刷新）
    // - 创建事件：仅当新工单指派给当前用户时推送并播放提示音
    const currentUserId = userStore?.user?.id
    const dataPayload = notification?.data?.data || notification?.data || {}
    const nType = notification?.type || dataPayload?.type || dataPayload?.event || dataPayload?.action
    const status = dataPayload?.status
    const assigneeId = notification?.assignee_id ?? dataPayload?.assignee_id

    const TYPE_CREATED_SET = new Set(['ticket_created', 'store_new_ticket', 'new_ticket', 'created'])
    const TYPE_COMPLETED_SET = new Set(['ticket_completed', 'completed', 'ticket_done', 'done'])

    const isTicketCreated = (nType && TYPE_CREATED_SET.has(String(nType))) || dataPayload?.action === 'created'
    const isTicketCompleted = (nType && TYPE_COMPLETED_SET.has(String(nType))) || status === 'completed' || dataPayload?.action === 'completed'
    const isAssignedToMe = !!currentUserId && !!assigneeId && assigneeId === currentUserId

    // 任何“工单完成”事件：直接忽略
    if (isTicketCompleted) {
      return
    }

    // 新工单且指派给我：推送并播放提示音
    if (isTicketCreated && isAssignedToMe) {
      addNotification(notification, false, true, false)
    } else {
      // 其它情况：静默入列，不播放提示音（仍可触发刷新）
      addNotification(notification, false, false, false)
    }
  }
}

// 添加新通知
const addNotification = (notificationData, showBrowserNotification = false, playSound = false, suppressRefresh = false) => {
  notifications.value.unshift({
    id: notificationData.id,
    title: notificationData.title,
    message: notificationData.message,
    created_at: notificationData.created_at || new Date().toISOString(),
    is_read: false,
    data: notificationData.data
  })
  
  // 如果有未读覆盖值，增加未读总数
  if (unreadOverride.value !== null) {
    unreadOverride.value += 1
  }
  
  // 播放提示音（仅实时新消息时）
  if (playSound) {
    playNotificationSound()
  }
  
  // 工单相关：按需触发刷新（历史未读不触发）
  const ticketId = notificationData.data?.data?.ticket_id || notificationData.data?.ticket_id
  if (ticketId && !suppressRefresh) {
    console.log('触发工单列表刷新事件，工单ID:', ticketId)
    eventBus.emit(EVENTS.TICKET_NOTIFICATION_RECEIVED, {
      ticketId,
      notification: notificationData
    })
  }
  
  // 只有在明确指定时才显示浏览器通知，避免重复通知
  if (showBrowserNotification && !notificationData.is_read && 'Notification' in window) {
    // 请求通知权限（如果尚未授权）
    if (Notification.permission === 'default') {
      Notification.requestPermission()
    }
    
    // 如果已授权，显示浏览器通知
    if (Notification.permission === 'granted') {
      const browserNotification = new Notification(notificationData.title, {
        body: notificationData.message,
        icon: '/favicon.ico',
        tag: `notification-${notificationData.id}`, // 防止重复通知
        requireInteraction: true // 需要用户交互才会消失
      })
      
      // 点击通知时跳转到相关页面
      browserNotification.onclick = () => {
        window.focus()
        const ticketId = notificationData.data?.data?.ticket_id || notificationData.data?.ticket_id
        if (ticketId) {
          router.push(`/ticket-detail/${ticketId}`)
        }
        browserNotification.close()
      }
      
      // 5秒后自动关闭（如果用户没有交互）
      setTimeout(() => {
        browserNotification.close()
      }, 5000)
    }
  }
}

// 方法
const togglePanel = () => {
  open.value = !open.value
}

const handleNotificationClick = (notification) => {
  // 标记为已读
  if (!notification.is_read) {
    notification.is_read = true
    // 调用API标记为已读
    markNotificationRead(notification.id)
  }
  
  // 跳转到相关页面
  const ticketId = notification.data?.data?.ticket_id || notification.data?.ticket_id
  if (ticketId) {
    router.push(`/ticket-detail/${ticketId}`)
    open.value = false
  }
}

const markNotificationRead = async (notificationId) => {
  try {
    // 调用真实API标记通知为已读
    await api.post(`/tickets/notifications/${notificationId}/mark_as_read/`)
  } catch (error) {
    console.error('标记通知已读失败:', error)
  }
}

const markAllRead = async () => {
  try {
    // 调用真实API标记所有通知为已读
    await api.post('/tickets/notifications/mark_all_read/')
    
    // 更新本地数据
    const currentTime = new Date().toISOString()
    notifications.value.forEach(notification => {
      if (!notification.is_read) {
        notification.is_read = true
        notification.read_at = currentTime
      }
    })
    
    // 将未读覆盖值设为0
    unreadOverride.value = 0
    
    message.success('已将所有通知标记为已读')
  } catch (error) {
    console.error('标记全部已读失败:', error)
    message.error('标记已读失败，请稍后重试')
  }
}

const viewAllNotifications = () => {
  open.value = false
  router.push('/notifications')
}

const formatTime = (timeStr) => {
  const now = new Date()
  const time = new Date(timeStr)
  // 确保使用东八时区进行计算
  const diff = now.getTime() - time.getTime()
  
  const minutes = Math.floor(diff / (1000 * 60))
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  
  if (minutes < 1) return '刚刚'
  if (minutes < 60) return `${minutes}分钟前`
  if (hours < 24) return `${hours}小时前`
  if (days < 7) return `${days}天前`
  
  return time.toLocaleDateString('zh-CN', { timeZone: 'Asia/Shanghai' })
}

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768
}

// 生命周期
onMounted(() => {
  connectWebSocket()
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  if (ws.value) {
    ws.value.close()
  }
  window.removeEventListener('resize', checkMobile)
})
</script>

<style scoped>
.notification-panel {
  position: fixed;
  top: 80px;
  right: 20px;
  width: 360px;
  max-height: 500px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  border: 1px solid #f0f0f0;
  overflow: hidden;
}

.notification-panel.mobile {
  left: 20px;
  right: 20px;
  width: auto;
  top: 70px;
}

.notification-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  color: #262626;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.notification-list {
  max-height: 350px;
  overflow-y: auto;
}

.notification-item {
  padding: 12px 20px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.3s;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.notification-item:hover {
  background-color: #fafafa;
}

.notification-item.unread {
  background-color: #f6ffed;
  border-left: 3px solid #52c41a;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-content {
  flex: 1;
}

.notification-title {
  font-weight: 500;
  color: #262626;
  margin-bottom: 4px;
  font-size: 14px;
}

.notification-message {
  color: #666;
  font-size: 13px;
  line-height: 1.4;
  margin-bottom: 4px;
}

.notification-time {
  color: #999;
  font-size: 12px;
}

.unread-dot {
  width: 8px;
  height: 8px;
  background: #1890ff;
  border-radius: 50%;
  margin-left: 8px;
  margin-top: 6px;
  flex-shrink: 0;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.empty-state p {
  margin-top: 8px;
  margin-bottom: 0;
}

.notification-footer {
  padding: 12px 20px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.footer-info {
  flex: 1;
}

.info-text {
  font-size: 12px;
  color: #999;
  font-style: italic;
}

.notification-trigger {
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

@media (max-width: 768px) {
  .notification-trigger {
    bottom: 20px;
    right: 20px;
  }
}
</style>